import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Archive, Package } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import { ProductForm } from "@/components/forms/ProductForms";
import BulkImportProductsButton from "@/components/forms/BulkImportProductsButton";
import BulkDeleteButton from "@/components/BulkDeleteButton";
import { SearchFilters } from "@/components/SearchFilters";
import { Pagination } from "@/components/Pagination";
import { TableSkeleton } from "@/components/Skeleton";
import { PRODUCT_STATUSES } from "@/lib/constants";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PAGE_SIZE = 25;

export default function ProductsListPage() {
  const { user } = useAuth();
  const isStaff = user?.role !== "client_user";
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [clients, setClients] = useState([]);
  const [types, setTypes] = useState(["DSK"]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);

  const page = Math.max(1, parseInt(params.get("page") || "1"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {
        q: params.get("q") || undefined,
        status: params.get("status") || undefined,
        client: params.get("client") || undefined,
        include_archived: params.get("archived") === "1" || undefined,
        page,
        page_size: PAGE_SIZE,
      };
      const [pRes, cRes, tRes] = await Promise.all([
        api.get("/products", { params: q }),
        isStaff ? api.get("/clients") : Promise.resolve({ data: [] }),
        api.get("/product-types"),
      ]);
      setData(pRes.data);
      setClients((cRes.data || []).filter((c) => !c.is_archived));
      setTypes(tRes.data || ["DSK"]);
      setSelectedIds([]);
    } finally { setLoading(false); }
  }, [params, page, isStaff]);

  useEffect(() => { load(); }, [load]);

  const toggleOne = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allIds = data.items.map((p) => p.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : allIds);

  const colCount = (isAdmin ? 1 : 0) + (isStaff ? 8 : 7);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Products / Inventory</h2>
          <p className="text-muted-foreground">{data.total} product{data.total === 1 ? "" : "s"}</p>
        </div>
        {isStaff && clients.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && selectedIds.length > 0 && (
              <BulkDeleteButton
                selectedIds={selectedIds}
                resource="products"
                endpoint="/products/bulk-delete"
                testIdRoot="products"
                onDeleted={load}
              />
            )}
            {isAdmin && (
              <Button asChild size="sm" variant="outline" data-testid="archived-products-link">
                <Link to="/products/archived">
                  <Archive className="mr-1 h-3.5 w-3.5" /> Archived Products
                </Link>
              </Button>
            )}
            <BulkImportProductsButton onImported={load} />
            <ProductForm clients={clients} types={types} onSaved={load} />
          </div>
        )}
      </div>
      {isStaff && clients.length === 0 && !loading && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          To get started, add a client on the{" "}
          <Link to="/clients" className="font-semibold underline hover:text-amber-950">Clients page</Link>
          {" "}— then you can add users under that client and start tracking products here.
        </div>
      )}
      <SearchFilters statuses={PRODUCT_STATUSES} clients={isStaff ? clients : null} showArchivedToggle={isStaff} placeholder="Search DSK or stick serial..." />
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={6} cols={colCount} />
          ) : data.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    {isAdmin && (
                      <th className="w-10 px-4 py-3">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={toggleAll}
                          aria-label="Select all products on this page"
                          data-testid="products-select-all"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 font-medium">Serial Number</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    {isStaff && <th className="px-4 py-3 font-medium">Organization</th>}
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Current Location</th>
                    <th className="px-4 py-3 font-medium">Warehouse Spot</th>
                    <th className="px-4 py-3 font-medium">Received</th>
                    <th className="px-4 py-3 font-medium">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((p) => (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50">
                      {isAdmin && (
                        <td className="px-4 py-3">
                          <Checkbox
                            checked={selectedIds.includes(p.id)}
                            onCheckedChange={() => toggleOne(p.id)}
                            aria-label={`Select product ${p.serial_number}`}
                            data-testid={`products-row-select-${p.id}`}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3"><Link to={`/products/${p.id}`} className="font-medium text-primary hover:underline">{p.serial_number}</Link></td>
                      <td className="px-4 py-3">{p.product_type}</td>
                      {isStaff && <td className="px-4 py-3">{p.client_name || "—"}</td>}
                      <td className="px-4 py-3">
                        <StatusBadge status={p.status} />
                        {p.is_archived && <span className="ml-1 inline-flex items-center rounded-md bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">Archived</span>}
                      </td>
                      <td className="px-4 py-3">{p.current_location}</td>
                      <td className="px-4 py-3">{p.warehouse_location || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{p.received_date || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{new Date(p.updated_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 p-10 text-center text-muted-foreground">
              <Package className="h-5 w-5" />
              <p>No products match. Adjust filters or add a product.</p>
            </div>
          )}
        </CardContent>
      </Card>
      <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} />
    </div>
  );
}
